'use client';

import { trackUserClick } from '@hc-frontend/third-party-snowplow';
import React, { useState } from 'react';

import { useConsent } from './use-consent';

export interface ConsentBannerProps {
  /**
   * URL to the privacy policy
   * Defaults to healthcare.com production privacy policy
   */
  privacyPolicyUrl?: string;
}

/**
 * CCPA-compliant consent banner for California users
 * Allows users to choose between all cookies or required cookies only
 */
export function ConsentBanner({
  privacyPolicyUrl = 'https://www.healthcare.com/privacy-policy#additional-information-about-our-data-collection-and-sharing-practices',
}: ConsentBannerProps) {
  const [open, setOpen] = useState(true);
  const { status, isLoading, saveConsent } = useConsent();

  // Don't show if user has already made a choice or still loading
  if (isLoading || status !== 'unknown') {
    return null;
  }

  // Don't show if manually closed
  if (!open) {
    return null;
  }

  const handleAllCookies = (event: React.MouseEvent<HTMLButtonElement>) => {
    trackUserClick({
      elementId: event.currentTarget.id,
      label: 'Allow All Cookies',
      value: 'all_cookies',
      action: 'click',
      category: 'consent',
      placement: 'banner',
    });

    saveConsent('all_cookies');
    setOpen(false);
  };

  const handleRequiredOnly = (event: React.MouseEvent<HTMLButtonElement>) => {
    trackUserClick({
      elementId: event.currentTarget.id,
      label: 'Required Cookies Only',
      value: 'required_cookies',
      action: 'click',
      category: 'consent',
      placement: 'banner',
    });

    saveConsent('required_cookies');
    setOpen(false);
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-20 rounded-t bg-primary-900 text-tertiary-50 sm:bottom-16-16 sm:left-auto sm:right-16-16 sm:max-w-[30rem] sm:rounded">
      <div className="flex items-start justify-center px-12-24 py-12-16">
        <div className="flex-1">
          <p className="mb-8-12">
            We use cookies to improve your experience and analyze site traffic.
            Some cookies are essential for the site to function, while others
            are optional and help us personalize content and deliver
            advertising. California residents can opt out of the sale or sharing
            of personal information by allowing only the required cookies.{' '}
            <a
              href={privacyPolicyUrl}
              target="_blank"
              className="font-bold text-tertiary-50 underline"
              rel="noopener noreferrer"
            >
              Learn more
            </a>
            .
          </p>
          <div className="flex flex-col gap-4-8 sm:flex-row">
            <button
              className="rounded bg-tertiary-50 px-16-16 py-8-8 text-sm font-bold text-primary-900 hover:bg-tertiary-100"
              id="data-consent-all-cookies"
              onClick={handleAllCookies}
              type="button"
            >
              Allow All Cookies
            </button>
            <button
              className="rounded border border-tertiary-50 px-16-16 py-8-8 text-sm font-bold text-tertiary-50 hover:bg-primary-800"
              id="data-consent-required-only"
              onClick={handleRequiredOnly}
              type="button"
            >
              Required Cookies Only
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
